import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";
import { IMG } from "@/lib/platform";
import { trackLead } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InterestSection() {
  const [form, setForm] = useState({ name: "", email: "", city: "sofia", hp: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "busy") return;
    if (!consent) {
      setStatus("error");
      setError("Моля, потвърди съгласието за обработка на данните.");
      return;
    }
    setStatus("busy");
    setError("");
    try {
      const r = await axios.post(`${API}/interest`, {
        name: form.name,
        email: form.email,
        city: form.city,
        source: "interest-section",
        consent: true,
        hp: form.hp,
      });
      if (r.data?.success) {
        trackLead({ email: form.email, content_name: `Interest ${form.city}`, content_category: "city-interest" });
        setStatus("done");
      } else {
        setStatus("error");
        setError("Провери полетата и опитай пак.");
      }
    } catch {
      setStatus("error");
      setError("Нещо се обърка. Опитай отново след малко.");
    }
  };

  return (
    <section data-testid="interest-section" className="relative bg-[#0B1F3D] text-[#F5EBD9] py-24 lg:py-36 overflow-hidden">
      <div
        className="absolute inset-0 opacity-[0.07] bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: `url(${IMG.sofia})` }}
      />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9 }}
        >
          <div className="inline-flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.32em] text-[#F5892E] mb-5">
            <span className="h-px w-10 bg-[#F5892E]" />
            София · Пловдив
            <span className="h-px w-10 bg-[#F5892E]" />
          </div>
          <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
            Играта идва и в <span className="text-[#F5892E]">твоя град.</span>
          </h2>
          <p className="mt-6 text-lg text-[#C8D4E6] leading-relaxed max-w-[52ch] mx-auto">
            София и Пловдив са следващите спирки на „Играта на имотите". Остави имейла си и научи
            първи, когато обявим датите и билетите.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-12"
        >
          {status === "done" ? (
            <div data-testid="interest-success" className="inline-flex items-center gap-3 border border-[#F5892E]/50 bg-[#F5892E]/10 px-7 py-5 rounded-2xl">
              <CheckCircle2 className="w-6 h-6 text-[#F5892E] shrink-0" strokeWidth={2} />
              <span className="font-display uppercase text-lg text-[#F5892E] text-left">
                Записан си! Ще научиш първи.
              </span>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="interest-form" className="max-w-xl mx-auto space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  type="text"
                  required
                  placeholder="Име"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="interest-name"
                  className="w-full px-5 py-4 rounded-xl bg-[#0F284B] border border-[#1E3A63] text-[#F5EBD9] placeholder:text-[#8A9EB8] focus:outline-none focus:border-[#F5892E] transition-colors"
                />
                <input
                  type="email"
                  required
                  placeholder="Имейл"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="interest-email"
                  className="w-full px-5 py-4 rounded-xl bg-[#0F284B] border border-[#1E3A63] text-[#F5EBD9] placeholder:text-[#8A9EB8] focus:outline-none focus:border-[#F5892E] transition-colors"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { id: "sofia", label: "София" },
                  { id: "plovdiv", label: "Пловдив" },
                ].map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setForm({ ...form, city: c.id })}
                    data-testid={`interest-city-${c.id}`}
                    className={`py-4 rounded-xl font-display uppercase text-xl tracking-tight border transition-all ${
                      form.city === c.id
                        ? "border-[#F5892E] bg-[#F5892E]/15 text-[#F5892E]"
                        : "border-[#1E3A63] text-[#8A9EB8] hover:border-[#F5892E]/50"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={form.hp}
                onChange={(e) => setForm({ ...form, hp: e.target.value })}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />
              <label className="flex items-start gap-3 text-xs text-[#8A9EB8] leading-relaxed cursor-pointer text-left max-w-xl mx-auto">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="interest-consent"
                  className="mt-0.5 w-4 h-4 accent-[#F5892E] shrink-0"
                />
                Съгласен/на съм данните ми да бъдат обработени, за да получа информация за събитията.
              </label>
              {status === "error" && (
                <div className="text-sm text-[#E5484D]" data-testid="interest-error">{error}</div>
              )}
              <button
                type="submit"
                disabled={status === "busy"}
                data-testid="interest-submit"
                className="w-full sm:w-auto px-10 py-4.5 py-4 rounded-full bg-[#F5892E] text-[#0B1F3D] font-bold text-sm uppercase tracking-widest hover:bg-[#F5EBD9] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2.5"
              >
                {status === "busy" ? "Записвам..." : "Искам да науча първи"}
                <ArrowRight className="w-4 h-4" strokeWidth={2.6} />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
